
/**
 * Module dependencies.
 */

import { css } from 'styled-components';
import { media } from 'react-components/styles';

/**
 * Font families.
 */

const fontFamilies = {
  inter: '"Inter", sans-serif',
  saira: '"Saira", sans-serif'
};

/**
 * Heading 1.
 */

const heading1 = css`
  font-family: ${fontFamilies.saira};
  font-size: 48px;
  font-weight: 300;
  letter-spacing: -1px;
  line-height: 52px;

  ${media.min('ms')`
    font-size: 79px;
    letter-spacing: -3px;
    line-height: 80px;
  `}
`;

/**
 * Heading 2.
 */

const heading2 = css`
  font-family: ${fontFamilies.saira};
  font-size: 40px;
  font-weight: 400;
  letter-spacing: -1px;
  line-height: 44px;

  ${media.min('ms')`
    font-size: 56px;
    font-weight: 300;
    letter-spacing: -2px;
    line-height: 56px;
  `}
`;

/**
 * Heading 3.
 */

const heading3 = css`
  font-family: ${fontFamilies.saira};
  font-size: 28px;
  font-weight: 400;
  letter-spacing: -0.5px;
  line-height: 32px;

  ${media.min('ms')`
    font-size: 40px;
    letter-spacing: -1px;
    line-height: 48px;
  `}
`;

/**
 * Heading 4.
 */

const heading4 = css`
  font-family: ${fontFamilies.saira};
  font-size: 24px;
  font-weight: 400;
  letter-spacing: -0.5px;
  line-height: 28px;

  ${media.min('ms')`
    font-size: 28px;
    line-height: 32px;
  `}
`;

/**
 * Text large.
 */

const textLarge = css`
  font-family: ${fontFamilies.inter};
  font-size: 20px;
  font-weight: 400;
  letter-spacing: -0.5px;
  line-height: 24px;

  ${media.min('ms')`
    font-size: 22px;
    line-height: 26px;
  `}
`;

/**
 * Text medium.
 */

const textMedium = css`
  font-family: ${fontFamilies.inter};
  font-size: 18px;
  font-weight: 400;
  letter-spacing: -0.5px;
  line-height: 24px;

  ${media.min('ms')`
    font-size: 20px;
    line-height: 26px;
  `}
`;

/**
 * Text normal.
 */

const textNormal = css`
  font-family: ${fontFamilies.inter};
  font-size: 16px;
  font-weight: 400;
  letter-spacing: -0.5px;
  line-height: 22px;
`;

/**
 * Text small.
 */

const textSmall = css`
  font-family: ${fontFamilies.inter};
  font-size: 13px;
  font-weight: 400;
  letter-spacing: -0.5px;
  line-height: 19px;

  ${media.min('ms')`
    font-size: 14px;
    line-height: 20px;
  `}
`;

/**
 * Text extra small.
 */

const textExtraSmall = css`
  font-family: ${fontFamilies.inter};
  font-size: 11px;
  font-weight: 400;
  letter-spacing: -0.5px;
  line-height: 15px;
`;

/**
 * Caption medium.
 */

const captionMedium = css`
  font-family: ${fontFamilies.saira};
  font-size: 16px;
  font-weight: 400;
  letter-spacing: -0.2px;
  line-height: 22px;
`;

/**
 * Caption small.
 */

const captionSmall = css`
  font-family: ${fontFamilies.saira};
  font-size: 14px;
  font-weight: 400;
  letter-spacing: -0.2px;
  line-height: 20px;
`;

/**
 * Caption extra small.
 */

const captionExtraSmall = css`
  font-family: ${fontFamilies.saira};
  font-size: 12px;
  font-weight: 400;
  letter-spacing: -0.2px;
  line-height: 18px;
`;

/**
 * Export `typography` styles.
 */

export const typography = {
  fontFamilies,
  styles: {
    captionExtraSmall,
    captionMedium,
    captionSmall,
    heading1,
    heading2,
    heading3,
    heading4,
    textExtraSmall,
    textLarge,
    textMedium,
    textNormal,
    textSmall
  }
};
